import React from "react";
import { emailBuilderViewportBuckets } from "./emailBuilderViewportBuckets";

export interface IEmailBuilderViewportState {
  width: number | undefined;
  height: number | undefined;
  isMobileLayout: boolean;
  usesBottomSheetChrome: boolean;
  usesCompactSettingsChrome: boolean;
  usesDesktopRightSettingsPanel: boolean;
}

const EmailBuilderViewportContext = React.createContext<IEmailBuilderViewportState | null>(null);

function readEmailBuilderViewportState(): IEmailBuilderViewportState {
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    ...emailBuilderViewportBuckets(width),
  };
}

function emailBuilderViewportBucketsEqual(
  left: IEmailBuilderViewportState,
  right: IEmailBuilderViewportState,
): boolean {
  return (
    left.isMobileLayout === right.isMobileLayout &&
    left.usesBottomSheetChrome === right.usesBottomSheetChrome &&
    left.usesCompactSettingsChrome === right.usesCompactSettingsChrome &&
    left.usesDesktopRightSettingsPanel === right.usesDesktopRightSettingsPanel
  );
}

export function EmailBuilderViewportProvider(props: { children: React.ReactNode }) {
  const [state, setState] = React.useState<IEmailBuilderViewportState>(() =>
    readEmailBuilderViewportState(),
  );

  React.useEffect(() => {
    let rafId = 0;

    function commitViewportState() {
      rafId = 0;
      const next = readEmailBuilderViewportState();
      setState(prev => (emailBuilderViewportBucketsEqual(prev, next) ? prev : next));
    }

    function handleResize() {
      if (rafId !== 0) {
        return;
      }
      rafId = requestAnimationFrame(commitViewportState);
    }

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <EmailBuilderViewportContext.Provider value={state}>
      {props.children}
    </EmailBuilderViewportContext.Provider>
  );
}

export function useEmailBuilderViewport(): IEmailBuilderViewportState {
  const value = React.useContext(EmailBuilderViewportContext);
  if (value === null) {
    throw new Error("useEmailBuilderViewport must be used within EmailBuilderViewportProvider");
  }
  return value;
}
